import React, { useEffect, useState } from "react";
import axios from "axios";
import { useSearchParams, Link } from "react-router-dom";
import { Landmark, Loader2, Tag, ShoppingCart, BadgeCheck, ArrowRight, X, FileText, Repeat } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => (n == null ? "—" : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const errMsg = (e) => {
  const d = e?.response?.data?.detail;
  if (Array.isArray(d)) return d.map((x) => x.msg || JSON.stringify(x)).join(" ");
  return d || e.message || "Something went wrong.";
};

export default function SecuritiesMarketplace() {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [buy, setBuy] = useState(null); // listing being purchased
  const [lend, setLend] = useState(null); // listing being lent as a securities loan
  const [lendForm, setLendForm] = useState({ counterparty_name: "", counterparty_entity: "", counterparty_email: "", term_days: 90, purpose: "" });
  const [lendDone, setLendDone] = useState(false);
  const [form, setForm] = useState({ buyer_name: "", buyer_email: "" });
  const [msg, setMsg] = useState("");
  const [cert, setCert] = useState(null); // purchased tradeline (for certificate download)
  const [filters, setFilters] = useState({ type: "all", minDiscount: "", maxFace: "", sort: "newest" });
  const [params] = useSearchParams();

  const load = async () => {
    setLoading(true);
    try { const r = await axios.get(`${API}/securities/marketplace`); setListings(r.data.listings || []); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    const sess = params.get("tl_session");
    if (sess) {
      axios.get(`${API}/securities/tradeline/status?session_id=${sess}`)
        .then((r) => {
          if (r.data.paid && r.data.tradeline) {
            setMsg(`✓ Tradeline ${r.data.tradeline.tradeline_no} purchased.`);
            setCert(r.data.tradeline);
          }
        })
        .catch(() => {});
    }
  }, []); // eslint-disable-line

  const types = React.useMemo(() => Array.from(new Set(listings.map((l) => l.instrument_type).filter(Boolean))), [listings]);
  const filtered = listings.filter((l) => {
    if (filters.type !== "all" && l.instrument_type !== filters.type) return false;
    if (filters.minDiscount && Number(l.discount_pct) < Number(filters.minDiscount)) return false;
    if (filters.maxFace && Number(l.face_amount) > Number(filters.maxFace)) return false;
    return true;
  }).sort((a, b) => {
    if (filters.sort === "discount") return Number(b.discount_pct) - Number(a.discount_pct);
    if (filters.sort === "face") return Number(b.face_amount) - Number(a.face_amount);
    return String(b.created_at || "").localeCompare(String(a.created_at || "")); // newest
  });

  const purchase = async (e) => {
    e.preventDefault();
    try {
      const { data } = await axios.post(`${API}/securities/listings/${buy.id}/buy-tradeline`, { origin_url: window.location.origin, ...form });
      window.location.href = data.url;
    } catch (err) { alert(errMsg(err)); }
  };

  const submitLend = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/securities/loan-requests`, {
        counterparty_name: lendForm.counterparty_name,
        counterparty_entity: lendForm.counterparty_entity,
        counterparty_email: lendForm.counterparty_email,
        amount: lend.face_amount,
        instrument_type: lend.instrument_type,
        term_days: Number(lendForm.term_days) || 90,
        purpose: lendForm.purpose,
        listing_id: lend.id,
        source: "marketplace",
      });
      setLendDone(true);
    } catch (err) { alert(errMsg(err)); }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#ECECE4]" data-testid="securities-marketplace-page">
      <header className="px-6 sm:px-10 py-5 flex items-center justify-between border-b border-[#1C1C16]">
        <div>
          <div className="font-serif text-[20px]">SEUTRADE FINANCIAL SERVICES</div>
          <div className="text-[9.5px] tracking-[0.24em] uppercase text-[#C9A227] mt-1">Securities Marketplace · Instruments &amp; Tradelines</div>
        </div>
        <div className="flex items-center gap-4 text-[11px] uppercase tracking-[0.14em]">
          <Link to="/securities/my-tradelines" className="text-[#C9A227] hover:text-[#e0b62e]" data-testid="marketplace-my-tradelines-link">My Tradelines</Link>
          <Link to="/securities" className="text-[#8A8A8A] hover:text-[#ECECE4]">Securities Desk</Link>
          <Link to="/" className="text-[#8A8A8A] hover:text-[#ECECE4]">Home</Link>
        </div>
      </header>

      <main className="px-6 sm:px-10 py-10 max-w-5xl mx-auto">
        <h1 className="font-serif text-3xl sm:text-4xl">Credit Instruments <span className="text-[#C9A227]">at a discount.</span></h1>
        <p className="text-[#E4C97A] text-base sm:text-lg mt-3 font-medium" data-testid="marketplace-tagline">Boost your corporate credit — grow your PAYDEX score today.</p>
        <p className="text-[#9A9A90] text-sm mt-3 max-w-2xl">Each facility is structured as a <strong>Private Negotiable Instrument</strong> offered below face value. Purchase a <strong>tradeline position</strong> for <strong>1.275% of the list amount</strong> — <strong>or lend it as a securities loan</strong>. Settled securely via Stripe.</p>
        {msg && (
          <div className="mt-5 rounded-md border border-[#0B6B4F]/50 bg-[#0B6B4F]/10 text-[#3DBB8E] text-[13px] px-4 py-3 flex flex-wrap items-center justify-between gap-3" data-testid="marketplace-msg">
            <span>{msg}</span>
            {cert && (
              <a href={`${API}/securities/tradeline/certificate.pdf?t=${cert.verify_token}`} target="_blank" rel="noreferrer" data-testid="tradeline-certificate-btn"
                className="inline-flex items-center gap-1.5 bg-[#0B6B4F]/30 hover:bg-[#0B6B4F]/50 text-[#3DBB8E] px-3 py-1.5 rounded-md text-[11px] uppercase tracking-[0.12em] font-bold">
                <FileText size={13} /> Download certificate
              </a>
            )}
          </div>
        )}

        {!loading && listings.length > 0 && (
          <div className="mt-8 flex flex-wrap items-end gap-4" data-testid="marketplace-filters">
            <div>
              <label className="block text-[9.5px] uppercase tracking-[0.14em] text-[#8A8A8A] mb-1.5">Instrument type</label>
              <select value={filters.type} onChange={(e) => setFilters((f) => ({ ...f, type: e.target.value }))} data-testid="filter-type"
                className="bg-[#0F0F0A] border border-[#2A2A22] rounded-md px-3 py-2 text-[13px] text-[#ECECE4] focus:border-[#C9A227] outline-none min-w-[220px]">
                <option value="all">All types</option>
                {types.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[9.5px] uppercase tracking-[0.14em] text-[#8A8A8A] mb-1.5">Min discount %</label>
              <input type="number" min="0" placeholder="e.g. 6" value={filters.minDiscount} onChange={(e) => setFilters((f) => ({ ...f, minDiscount: e.target.value }))} data-testid="filter-min-discount"
                className="bg-[#0F0F0A] border border-[#2A2A22] rounded-md px-3 py-2 text-[13px] text-[#ECECE4] focus:border-[#C9A227] outline-none w-[130px]" />
            </div>
            <div>
              <label className="block text-[9.5px] uppercase tracking-[0.14em] text-[#8A8A8A] mb-1.5">Max face ($)</label>
              <input type="number" min="0" placeholder="e.g. 1000000" value={filters.maxFace} onChange={(e) => setFilters((f) => ({ ...f, maxFace: e.target.value }))} data-testid="filter-max-face"
                className="bg-[#0F0F0A] border border-[#2A2A22] rounded-md px-3 py-2 text-[13px] text-[#ECECE4] focus:border-[#C9A227] outline-none w-[160px]" />
            </div>
            <div>
              <label className="block text-[9.5px] uppercase tracking-[0.14em] text-[#8A8A8A] mb-1.5">Sort by</label>
              <select value={filters.sort} onChange={(e) => setFilters((f) => ({ ...f, sort: e.target.value }))} data-testid="filter-sort"
                className="bg-[#0F0F0A] border border-[#2A2A22] rounded-md px-3 py-2 text-[13px] text-[#ECECE4] focus:border-[#C9A227] outline-none min-w-[170px]">
                <option value="newest">Newest</option>
                <option value="discount">Biggest discount</option>
                <option value="face">Highest face</option>
              </select>
            </div>
            {(filters.type !== "all" || filters.minDiscount || filters.maxFace || filters.sort !== "newest") && (
              <button onClick={() => setFilters({ type: "all", minDiscount: "", maxFace: "", sort: "newest" })} data-testid="filter-clear"
                className="text-[11px] uppercase tracking-[0.12em] text-[#8A8A8A] hover:text-[#ECECE4] py-2">Clear</button>
            )}
          </div>
        )}

        {loading ? <Loader2 className="animate-spin text-[#8A8A8A] mt-8" /> : filtered.length === 0 ? (
          <p className="text-[#8A8A8A] text-sm mt-8" data-testid="marketplace-empty">No instruments match your filters.</p>
        ) : (
          <div className="mt-8 grid sm:grid-cols-2 gap-5">
            {filtered.map((l) => (
              <div key={l.id} className="rounded-lg border border-[#2A2A22] bg-[#13130E] p-6" data-testid={`marketplace-listing-${l.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.14em] text-[#C9A227]"><Landmark size={13} /> {l.instrument_type}</div>
                  {l.registered && <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.12em] text-[#3DBB8E] border border-[#0B6B4F]/50 rounded-full px-2 py-0.5"><BadgeCheck size={10} /> Registered</span>}
                </div>
                {l.company && <Link to={`/securities/instrument/${l.id}`} data-testid={`marketplace-details-${l.id}`} className="text-[13px] text-[#ECECE4] hover:text-[#C9A227] mt-2 font-medium block transition-colors">{l.company} →</Link>}
                <div className="font-mono text-[11px] text-[#8A8A8A] mt-1">{l.account_no}{l.duns_number ? ` · DUNS ${l.duns_number}` : ""}</div>
                <div className="mt-4 grid grid-cols-3 gap-3 text-[13px]">
                  <div><div className="text-[10px] uppercase tracking-[0.12em] text-[#8A8A8A]">Face</div><div className="font-serif text-base">{fmt(l.face_amount)}</div></div>
                  <div><div className="text-[10px] uppercase tracking-[0.12em] text-[#8A8A8A]">Rate</div><div className="font-serif text-base text-[#E4C97A]">{l.discount_pct}%</div></div>
                  <div><div className="text-[10px] uppercase tracking-[0.12em] text-[#8A8A8A]">List price</div><div className="font-serif text-base text-[#3DBB8E]">{fmt(l.list_price)}</div></div>
                </div>
                <div className="mt-4 flex items-center justify-between gap-2">
                  <div className="text-[12px] text-[#9A9A90]"><Tag size={12} className="inline text-[#C9A227]" /> Tradeline: <strong className="text-[#E4C97A]">{fmt(l.tradeline_price)}</strong> (1.275%)</div>
                  <button onClick={() => { setBuy(l); setMsg(""); }} data-testid={`marketplace-buy-${l.id}`} className="inline-flex items-center gap-1.5 bg-[#C9A227] hover:bg-[#b8901f] text-[#0A0A0A] px-4 py-2 rounded-md text-[11px] uppercase tracking-[0.12em] font-bold"><ShoppingCart size={13} /> Buy tradeline</button>
                </div>
                <button onClick={() => { setLend(l); setMsg(""); }} data-testid={`marketplace-lend-${l.id}`} className="mt-3 w-full inline-flex items-center justify-center gap-1.5 border border-[#9DB4E8]/40 hover:border-[#9DB4E8] text-[#9DB4E8] hover:text-[#c3d3f5] px-4 py-2 rounded-md text-[11px] uppercase tracking-[0.12em] font-bold transition-colors"><Repeat size={13} /> Lend as Securities Loan</button>
              </div>
            ))}
          </div>
        )}

        <div className="mt-12 border-t border-[#1C1C16] pt-6 text-[11px] text-[#6C6C64] leading-relaxed max-w-3xl" data-testid="marketplace-disclaimer">
          <strong className="text-[#8A8A8A]">Disclaimer.</strong> Tradelines offered here are <strong>corporate credit-enhancement instruments</strong>, valid for 45 days and renewed by auto-debit of the holder's account. SEUTRADE FINANCIAL SERVICES is <strong>not a credit bureau</strong> and does not report to, or electronically file with, Dun &amp; Bradstreet, Experian Business, Equifax Business or any credit bureau. The platform generates filing-ready records (D&amp;B Business Trade Reference, UCC-1 Financing Statement); submission and any reporting to D&amp;B/DUNS is the holder's responsibility (or that of a D&amp;B-approved Trade Exchange reporter). Nothing here is an offer of securities, credit, or investment advice.
        </div>
      </main>

      {buy && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50" data-testid="marketplace-buy-modal">
          <div className="bg-[#13130E] border border-[#2A2A22] rounded-xl p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-4">
              <div className="font-serif text-xl">Purchase Tradeline</div>
              <button onClick={() => setBuy(null)} className="text-[#8A8A8A] hover:text-[#ECECE4]"><X size={18} /></button>
            </div>
            <p className="text-[13px] text-[#9A9A90] mb-4">{buy.instrument_type} · {buy.account_no} · Tradeline price <strong className="text-[#E4C97A]">{fmt(buy.tradeline_price)}</strong></p>
            <form onSubmit={purchase} className="space-y-3">
              <input required placeholder="Your full name" value={form.buyer_name} onChange={(e) => setForm((f) => ({ ...f, buyer_name: e.target.value }))} data-testid="marketplace-buyer-name"
                className="w-full bg-[#0F0F0A] border border-[#2A2A22] rounded-md px-3 py-2.5 text-[14px] text-[#ECECE4] focus:border-[#C9A227] outline-none" />
              <input required type="email" placeholder="Your email" value={form.buyer_email} onChange={(e) => setForm((f) => ({ ...f, buyer_email: e.target.value }))} data-testid="marketplace-buyer-email"
                className="w-full bg-[#0F0F0A] border border-[#2A2A22] rounded-md px-3 py-2.5 text-[14px] text-[#ECECE4] focus:border-[#C9A227] outline-none" />
              <button type="submit" data-testid="marketplace-pay" className="w-full inline-flex items-center justify-center gap-2 bg-[#C9A227] hover:bg-[#b8901f] text-[#0A0A0A] py-3 rounded-md text-[12px] uppercase tracking-[0.16em] font-bold">Pay {fmt(buy.tradeline_price)} <ArrowRight size={15} /></button>
            </form>
          </div>
        </div>
      )}

      {lend && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50" data-testid="marketplace-lend-modal">
          <div className="bg-[#13130E] border border-[#2A2A22] rounded-xl p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-4">
              <div className="font-serif text-xl">Lend as Securities Loan</div>
              <button onClick={() => { setLend(null); setLendDone(false); }} className="text-[#8A8A8A] hover:text-[#ECECE4]"><X size={18} /></button>
            </div>
            {lendDone ? (
              <div data-testid="marketplace-lend-done">
                <p className="text-[13px] text-[#3DBB8E] mb-3">✓ Request submitted. The SEUTRADE desk will review and issue a Securities Loan Agreement against this instrument (dual e-sign, origination fee, collateral encumbrance).</p>
                <button onClick={() => { setLend(null); setLendDone(false); setLendForm({ counterparty_name: "", counterparty_entity: "", counterparty_email: "", term_days: 90, purpose: "" }); }} className="text-[11px] uppercase tracking-[0.12em] text-[#C9A227] hover:text-[#e0b62e]">Done</button>
              </div>
            ) : (
              <>
                <p className="text-[13px] text-[#9A9A90] mb-4">{lend.company ? `${lend.company} · ` : ""}{lend.instrument_type} · face <strong className="text-[#E4C97A]">{fmt(lend.face_amount)}</strong></p>
                <form onSubmit={submitLend} className="space-y-3">
                  <input required placeholder="Counterparty full name" value={lendForm.counterparty_name} onChange={(e) => setLendForm((f) => ({ ...f, counterparty_name: e.target.value }))} data-testid="lend-name"
                    className="w-full bg-[#0F0F0A] border border-[#2A2A22] rounded-md px-3 py-2.5 text-[14px] text-[#ECECE4] focus:border-[#C9A227] outline-none" />
                  <input placeholder="Entity / company (optional)" value={lendForm.counterparty_entity} onChange={(e) => setLendForm((f) => ({ ...f, counterparty_entity: e.target.value }))} data-testid="lend-entity"
                    className="w-full bg-[#0F0F0A] border border-[#2A2A22] rounded-md px-3 py-2.5 text-[14px] text-[#ECECE4] focus:border-[#C9A227] outline-none" />
                  <input required type="email" placeholder="Counterparty email" value={lendForm.counterparty_email} onChange={(e) => setLendForm((f) => ({ ...f, counterparty_email: e.target.value }))} data-testid="lend-email"
                    className="w-full bg-[#0F0F0A] border border-[#2A2A22] rounded-md px-3 py-2.5 text-[14px] text-[#ECECE4] focus:border-[#C9A227] outline-none" />
                  <div className="flex gap-3">
                    <input required type="number" min="1" placeholder="Term (days)" value={lendForm.term_days} onChange={(e) => setLendForm((f) => ({ ...f, term_days: e.target.value }))} data-testid="lend-term"
                      className="w-1/2 bg-[#0F0F0A] border border-[#2A2A22] rounded-md px-3 py-2.5 text-[14px] text-[#ECECE4] focus:border-[#C9A227] outline-none" />
                  </div>
                  <input placeholder="Purpose (optional)" value={lendForm.purpose} onChange={(e) => setLendForm((f) => ({ ...f, purpose: e.target.value }))} data-testid="lend-purpose"
                    className="w-full bg-[#0F0F0A] border border-[#2A2A22] rounded-md px-3 py-2.5 text-[14px] text-[#ECECE4] focus:border-[#C9A227] outline-none" />
                  <button type="submit" data-testid="lend-submit" className="w-full inline-flex items-center justify-center gap-2 bg-[#9DB4E8] hover:bg-[#c3d3f5] text-[#0A0A0A] py-3 rounded-md text-[12px] uppercase tracking-[0.16em] font-bold">Request securities loan <ArrowRight size={15} /></button>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
